'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { Tooltip } from '#pplx/frontend/aether/components/Tooltip/index.js';
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { LegacyTablerIconSize } from '#pplx/frontend/aether/primitives/IconPrimitive/iconSizing.js';
import { IconPrimitive } from '#pplx/frontend/aether/primitives/IconPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import LegacyButton from '#pplx/frontend/components/buttons/Button.js';
import CloseButton from '#pplx/frontend/components/buttons/CloseButton.js';
import TextButton from '#pplx/frontend/components/buttons/TextButton.js';
import { FocusTrap } from '#pplx/frontend/components/overlays/internal/FocusTrap.js';
import { ModalInternalContext } from '#pplx/frontend/components/overlays/ModalInternalContext.js';
import { Overlay } from '#pplx/frontend/components/overlays/Overlay.js';
import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
function getModalLayoutClassNames(variant) {
    return classNames({
        'md:rounded-xl md:w-[600px] max-w-screen-sm relative h-full max-h-[100vh] md:max-h-[95vh] overflow-auto': variant === 'default',
        'min-h-0 max-h-[70vh] rounded-t-xl bottom-0 inset-x-0 flex flex-col relative': variant === 'bottom-left-sheet',
        'w-screen md:h-screen h-[100dvh] flex flex-col relative': variant === 'full-sheet',
        'w-screen min-h-[60dvh] md:min-h-[60vh] max-h-[96dvh] md:max-h-[96vh] flex flex-col fixed bottom-0 inset-x-0': variant === 'bottom-sheet',
        'h-screen sm:min-w-0 sm:w-auto md:w-[40vw] md:min-w-[500px] top-0 right-0 fixed flex flex-col': variant === 'side-sheet',
        'max-h-[100vh] w-[90vw] rounded-l-xl flex flex-col relative': variant === 'wide',
        'h-[95vh] w-[95vw] rounded-xl flex flex-col relative': variant === 'full',
        'md:rounded-xl md:w-[600px] max-w-screen-lg relative overflow-auto max-h-[100vh]': variant === 'large',
        'md:rounded-xl md:w-[700px] max-w-screen-lg relative overflow-auto max-h-[100vh]': variant === 'xl',
        'md:rounded-xl md:w-[500px] max-w-screen-lg relative overflow-auto max-h-[100vh]': variant === 'medium',
        'md:rounded-xl md:w-[440px] relative overflow-auto max-h-[100vh]': variant === 'small',
        'md:rounded-xl md:w-[350px] relative overflow-auto max-h-[100vh]': variant === 'thin',
    });
}
function getModalShadowClassNames(variant) {
    return classNames('shadow-md', {
        'forced-colors:border forced-colors:border-[CanvasText]': variant === 'default',
    });
}
function getModalAnimationClassNames(variant, isClosing) {
    return classNames({
        'animate-in slide-in-from-right ease-outExpo': variant === 'side-sheet' && !isClosing,
        'animate-out slide-out-to-right ease-inExpo': variant === 'side-sheet' && isClosing,
        'animate-in slide-in-from-bottom ease-in': (variant === 'bottom-left-sheet' || variant === 'bottom-sheet') && !isClosing,
        'animate-out slide-out-to-bottom ease-out': (variant === 'bottom-left-sheet' || variant === 'bottom-sheet') && isClosing,
    });
}
const Modal = React.memo(function Modal({ 'aria-label': ariaLabel, title, subtitle, subtitleClassname, centerTitle, children, actionList = EMPTY_ARRAY, variant = 'default', onClose, noPadding = false, onIsClosing, titleContent, footerContent, icon, overlayVariant, titleLeadingButtonProps, titleTextVariant = 'page-title', headerClassname, footerClassname, renderCloseButton = true, closeButtonVariant, modalClassname, modalContentClassname, includeBlur = true, isOpen, ref, wrapperClassname, restoreFocus = true, forceColorScheme, ...overlayProps }) {
    const scrollContainerRef = useRef(null);
    const headerRef = useRef(null);
    const contentRef = useRef(null);
    const footerRef = useRef(null);
    const [shouldClose, setShouldClose] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    useEffect(() => {
        if (isOpen && !isModalOpen) {
            setIsModalOpen(true);
        }
        else if (!isOpen && isModalOpen && !shouldClose) {
            setShouldClose(true);
        }
    }, [isOpen, isModalOpen, shouldClose]);
    const modalClass = useMemo(() => classNames(modalClassname, 'bg-base overflow-y-auto fill-mode-both', getModalLayoutClassNames(variant), getModalShadowClassNames(variant), getModalAnimationClassNames(variant, isClosing)), [variant, isClosing, modalClassname]);
    const isBottomSheet = useMemo(() => variant === 'bottom-left-sheet' || variant === 'bottom-sheet', [variant]);
    const contentClass = useMemo(() => classNames('grow flex flex-col justify-center shrink-0', {
        'md:pt-4': title === undefined && !titleContent,
        'md:pb-8': actionList?.length === 0,
        'p-2': !noPadding && !isBottomSheet,
        'p-0': noPadding,
        // Padding for mobile bottom sheets, if safe area is not supported
        // Comet Mobile does not have safe area support yet
        'pb-8': isBottomSheet,
    }, modalContentClassname), [actionList?.length, noPadding, title, modalContentClassname, titleContent, isBottomSheet]);
    const actionEls = useMemo(() => actionList.map((action, idx) => _createElement(ButtonFactory, { ...action, idx: idx, key: idx })), [actionList]);
    useImperativeHandle(ref, () => ({
        scrollContainerRef,
        headerRef,
        contentRef,
        footerRef,
    }), [scrollContainerRef, headerRef, footerRef]);
    const handleIsClosing = useCallback(() => {
        onIsClosing?.();
        setIsClosing(true);
    }, [onIsClosing]);
    const handleOverlayClose = useCallback((reason) => {
        setIsClosing(false);
        setShouldClose(false);
        setIsModalOpen(false);
        onClose?.(reason);
    }, [onClose]);
    const handleXClick = useCallback(() => {
        setShouldClose(true);
    }, []);
    const { canOutsideClickClose } = useContext(ModalInternalContext);
    const modalChildren = useMemo(() => {
        return (_jsx(FocusTrap, { active: isModalOpen, restoreFocus: restoreFocus, children: _jsx("div", { children: variant === 'hide-chrome' ? (_jsx("div", { role: "dialog", "aria-modal": "true", "aria-label": ariaLabel, children: children })) : (_jsxs("div", { role: "dialog", "aria-modal": "true", "aria-label": ariaLabel, className: modalClass, ref: scrollContainerRef, children: [renderCloseButton && (_jsx("div", { className: "sticky top-0 h-0 z-[23]", children: _jsx("div", { className: "right-4 top-4 absolute", children: _jsx(CloseButton, { onClick: handleXClick, variant: closeButtonVariant }) }) })), _jsxs(BoxPrimitive, { className: classNames(wrapperClassname, 'flex min-h-0 w-screen grow flex-col md:w-[unset] md:grow-[unset] h-full', {
                                'px-4 pb-4': !noPadding,
                                'max-w-screen-sm': variant === 'default',
                                'max-w-screen-lg': variant === 'large' || variant === 'medium' || variant === 'xl',
                            }), children: [_jsxs(BoxPrimitive, { bg: "background", className: classNames(headerClassname, 'pt-4 sticky top-0 z-[22] w-full shrink-0', {
                                        'text-center': centerTitle,
                                        'h-10': !title && !titleContent,
                                    }), ref: headerRef, children: [_jsxs("div", { className: "p-2 flex items-center gap-1.5", children: [titleLeadingButtonProps && (_jsx(TextButton, { extraCSS: "-ml-2", ...titleLeadingButtonProps, size: "small", variant: "common" })), icon ? (_jsx(IconPrimitive, { icon: icon, className: "text-primary -ml-1 shrink-0", size: LegacyTablerIconSize.xl })) : null, title && (_jsx(TextPrimitive, { variant: titleTextVariant, className: classNames('text-pretty', {
                                                        'line-clamp-1 leading-none': icon,
                                                        'line-clamp-3': !icon,
                                                        'pt-8 grow': centerTitle,
                                                        'mr-8': !centerTitle,
                                                    }), children: title })), !title && titleContent] }), subtitle && (_jsx(TextPrimitive, { variant: "base", color: "light", lineClamp: 3, className: classNames('p-2 text-pretty', subtitleClassname), children: subtitle }))] }), children && (_jsx("div", { className: contentClass, ref: contentRef, children: children })), (actionEls?.length > 0 || footerContent) && (_jsxs(BoxPrimitive, { bg: "background", className: classNames('gap-x-2 p-2 pt-8 bottom-0 z-10 flex items-center', { 'justify-end': !footerContent }, { 'justify-between': footerContent }, {
                                        // TODO(PER-10465): We'll want to remove sticky
                                        // positioned elements in modals to prevent anchored UI
                                        // elements from being repositioned during scale
                                        // transform animations.
                                        sticky: true,
                                    }, footerClassname), ref: footerRef, children: [footerContent && _jsx("div", { className: "flex-1", children: footerContent }), actionEls] }))] })] })) }) }));
    }, [
        actionEls,
        ariaLabel,
        centerTitle,
        children,
        contentClass,
        footerClassname,
        footerContent,
        handleXClick,
        headerClassname,
        icon,
        isModalOpen,
        modalClass,
        noPadding,
        renderCloseButton,
        restoreFocus,
        closeButtonVariant,
        subtitle,
        subtitleClassname,
        title,
        titleContent,
        titleLeadingButtonProps,
        titleTextVariant,
        variant,
        wrapperClassname,
    ]);
    return (_jsx(Overlay, { forceColorScheme: forceColorScheme, includeBlur: includeBlur, childVariant: variant, shouldClose: shouldClose, onIsClosing: handleIsClosing, onClose: handleOverlayClose, variant: overlayVariant, isOpen: isModalOpen, canOutsideClickClose: canOutsideClickClose, ...overlayProps, children: modalChildren }));
});
// Map legacy variants to Aether equivalents where possible
const LEGACY_TO_AETHER_VARIANT = {
    primary: 'primary',
    common: 'tonal',
    border: 'secondary',
    inverted: 'primary',
};
const ButtonFactory = React.memo(function ButtonFactory({ idx, text, onClick, variant, disabled, isLoading, icon, tooltipText, track, }) {
    const aetherVariant = variant ? LEGACY_TO_AETHER_VARIANT[variant] : undefined;
    // Fall back to legacy Button for unsupported variants (rejected, rejecter, error, success, etc.)
    // TODO: Migrate to Aether Button once it supports destructive/warning variants
    if (variant && !aetherVariant) {
        const legacyButton = (_jsx(LegacyButton, { fullWidthMobile: true, variant: variant, text: text, disabled: disabled, isLoading: isLoading, chevronIcon: icon, onClick: onClick }, idx));
        if (tooltipText) {
            return (_jsx(Tooltip, { content: tooltipText, side: "top", children: legacyButton }));
        }
        return legacyButton;
    }
    // Wrapper div provides fullWidthMobile behavior (full width on mobile, auto on desktop)
    const button = (_jsx("div", { className: "w-full md:w-auto", children: _jsx(Button, { variant: aetherVariant ?? 'primary', disabled: disabled, isLoading: isLoading, trailingAccessory: icon, onClick: onClick, track: track, fullWidth: true, children: text ?? '' }) }, idx));
    if (tooltipText) {
        return (_jsx(Tooltip, { content: tooltipText, side: "top", children: button }));
    }
    return button;
});
export default Modal;
//# sourceMappingURL=Modal.js.map